// Anniversary ritual — the one celebratory moment on the dashboard (W36.0).
//
// Spec §2.8 is precise about this, and the mockup is explicitly NOT the target
// (spec §6.2): the mockup paints the seal with a `radial-gradient` and hardcoded
// hex values, which AGENTS.md §3 #17 forbids. Here every colour is a token.
//
// The four rules, and why each is written the way it is:
//
// 1. **Warm paper + gold hairline.** `bg-paper-deep` is already the deeper,
//    warmer paper (DESIGN §2.4); the hairline is `gold/30` rather than a new
//    colour. DESIGN §15 says colour is never the only signal, so the seal and
//    the border carry the meaning together.
// 2. **The 「時間」 seal presses once.** `ease-letter` 480 ms, `both` fill so it
//    does not flash back to scale(1.32) — and it runs **once**, never looping.
//    A looping seal is a notification, and §2.8 rejects that alongside particles
//    and full-screen celebrations.
// 3. **The day count turns gold.** `text-gold`, not a hex — spec §2.8 says the
//    gold is `--src-anniversary-rgb` and 「非另挑」. `tabular-nums` per DESIGN
//    §3.3 so the number does not reflow while it changes colour.
// 4. **Reduced motion collapses to instant.** DESIGN §5.3 / §15. The rule lives
//    in the stylesheet as `.ritual-seal` because Tailwind's `motion-reduce:`
//    variant cannot express "no animation at all" on a custom keyframe that
//    uses `both`.
//
// The seal is decorative: `aria-hidden` so screen readers are not told about a
// stamp. The celebration itself is carried by the content the caller renders.

import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

export interface AnniversaryRitualProps {
  /** Card content. Owns the celebration copy — see `DailyAnniversaryCard`. */
  children: ReactNode;
  /**
   * Screen-reader summary of the celebration, since the visual treatment is
   * colour + a decorative seal and neither carries meaning alone (DESIGN §15).
   */
  srLabel?: string;
  className?: string;
  /**
   * Drop this card's own background, border and shadow so the surface it is
   * rendered on is the surface. W43 `ADR-0109`: the dashboard hosts this inside an
   * `AccentCard`, and with both painting, the saturated fill read as a 16px frame
   * around a cream card.
   */
  bare?: boolean;
  "data-testid"?: string;
}

export function AnniversaryRitual({
  children,
  srLabel,
  className,
  bare = false,
  "data-testid": testId = "anniversary-ritual",
}: AnniversaryRitualProps) {
  return (
    <div
      data-testid={testId}
      data-ritual="anniversary"
      className={cn(
        // W43 `ADR-0109`: `bare` drops the card's own surface so an `AccentCard`
        // wrapping it is the surface. Without it the saturated fill only shows as
        // a 16px frame around a cream card — a "double card", which is the same
        // mistake as a pale tint on every row, just with the colour on the wrong
        // side of it.
        bare
          ? "relative overflow-hidden rounded-2xl"
          : // Warm paper: paper-deep is DESIGN §2.4's deeper, warmer surface.
            "relative overflow-hidden rounded-2xl border border-gold/30 bg-paper-deep shadow-card",
        className,
      )}
    >
      <div className="p-s-5">{children}</div>
      {srLabel ? <p className="sr-only">{srLabel}</p> : null}
      <span className="pointer-events-none absolute inset-x-0 top-0 flex justify-end p-s-4">
        <TimeSeal />
      </span>
    </div>
  );
}

/**
 * The 「時間」 seal. Exported so the countdown (non-ritual) variant can be
 * asserted on in tests without mounting the whole card.
 *
 * `h-11 w-11` (44 px) keeps it at the DESIGN §15 minimum touch-target size even
 * though it is not itself interactive.
 */
export function TimeSeal({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-testid="anniversary-seal"
      className={cn(
        "inline-flex h-11 w-11 items-center justify-center rounded-pill",
        "bg-gold font-display text-meta font-semibold tracking-wider text-paper-elevated",
        "shadow-modal",
        "ritual-seal",
        className,
      )}
    >
      時間
    </span>
  );
}
